import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import IntellendLogo from '@/components/IntellendLogo';
import axios from 'axios';

interface ConsentPageProps {
  bapName?: string;
  consentType?: string;
  redirectPath?: string;
  onConsentAccepted?: () => void | Promise<void>;
  onBack?: () => void;
}

const ConsentPage: React.FC<ConsentPageProps> = ({
  bapName = 'PhonePe',
  consentType = 'Data_Sharing_Consent',
  redirectPath = '/loan-application',
  onConsentAccepted,
  onBack
}) => {
  const navigate = useNavigate();
  const params = useParams();
  
  const [consentDetails, setConsentDetails] = useState<any>(null);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bapIdentifier, setBapIdentifier] = useState<string | null>(null);

  // Get BAP name from URL params or props
  const effectiveBapName = params.bapName || bapName;

  useEffect(() => {
    fetchConsentDetails();
    checkBapIdentifier();
  }, []);

  const checkBapIdentifier = async () => {
    try {
      const response = await axios.post('/api/check_bap_identifier_active_session/');
      if (response.data.bap_identifier) {
        setBapIdentifier(response.data.bap_identifier);
        console.log('[ConsentPage] BAP identifier found:', response.data.bap_identifier);
      } else {
        console.warn('[ConsentPage] No BAP identifier in session, redirecting to home');
        navigate(`/home/${effectiveBapName.toLowerCase()}`);
      }
    } catch (error) {
      console.error('[ConsentPage] Error checking BAP identifier:', error);
    }
  };

  const fetchConsentDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post('/api/get_consent_details/', {
        enc_id: 'WHITESPACE',
        consent_type: consentType
      });

      if (response.data) {
        setConsentDetails(response.data);
      }
    } catch (error) {
      console.error('[ConsentPage] Error fetching consent details:', error);
      setError('Failed to load consent details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isAccepted) {
      setError('Please accept the terms to continue');
      return;
    }

    if (!consentDetails) {
      setError('Consent details not loaded');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await axios.post('/api/submit_consent/', {
        enc_id: 'WHITESPACE',
        consent_details: {
          id: consentDetails.consent_details.id,
          type: consentDetails.consent_details.type,
          version: consentDetails.consent_details.version
        },
        accepts_terms_and_privacy: isAccepted
      });

      console.log('[ConsentPage] Consent submitted successfully:', response.data);

      if (onConsentAccepted) {
        await onConsentAccepted();
      } else {
        navigate(redirectPath);
      }
    } catch (error: any) {
      console.error('[ConsentPage] Error submitting consent:', error);
      setError(error.response?.data?.error || 'Failed to submit consent');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <IntellendLogo />
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Terms & Conditions
            </h1>
            <p className="text-gray-600">
              Please review and accept the terms to continue
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error && !consentDetails ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          ) : consentDetails ? (
            <>
              <div className="prose max-w-none">
                <div 
                  className="text-sm text-gray-700 space-y-4 max-h-96 overflow-y-auto border rounded-lg p-4"
                  dangerouslySetInnerHTML={{ 
                    __html: consentDetails.consent_details.text 
                  }}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Checkbox
                  id="consent-checkbox"
                  checked={isAccepted}
                  onCheckedChange={(checked) => setIsAccepted(checked as boolean)}
                />
                <label
                  htmlFor="consent-checkbox"
                  className="text-sm text-gray-700 cursor-pointer flex-1"
                >
                  I have read and accept the terms and conditions
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (onBack) {
                      onBack();
                    } else {
                      navigate(-1);
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isAccepted || isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Accept & Continue'}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </main>

      <footer className="bg-white border-t py-6">
        <div className="container mx-auto px-4 text-center text-sm text-gray-600">
          © {new Date().getFullYear()} Intellend. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ConsentPage;
